import { ProposalQuery } from 'generated/graphql';
import { formatCents } from 'utilities/money';

type LineItemFromQuery =
  ProposalQuery['proposal']['proposalOptions'][0]['hotels'][0]['lineItems'][0];

interface ProposalLineItemsProps {
  lineItems: LineItemFromQuery[];
  selectedLineItems: Record<string, boolean>;
  onLineItemToggle: (itemId: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

const ProposalLineItems = ({
  lineItems,
  selectedLineItems,
  onLineItemToggle,
  disabled = false,
  readOnly = false,
}: ProposalLineItemsProps) => {
  if (lineItems.length === 0) return null;

  // Filter to only selected items when in read-only mode
  const displayItems = readOnly
    ? lineItems.filter((item) => selectedLineItems[item.id] || item.selected)
    : lineItems;

  if (readOnly && displayItems.length === 0) return null;

  const calculateSubtotal = () => {
    return displayItems.reduce((total, item) => {
      return (
        total + (selectedLineItems[item.id] || item.selected ? item.price : 0)
      );
    }, 0);
  };

  return (
    <div className="rounded-xl border border-gray-200/50 bg-gray-50 p-4 sm:p-6">
      <h4 className="mb-4 font-oswald text-base font-bold text-gray-900 sm:text-lg">
        Additional Items
      </h4>

      <div className="space-y-3">
        {displayItems.map((item) =>
          readOnly ? (
            <div
              key={item.id}
              className="flex items-start space-x-3 rounded-lg bg-white p-3"
            >
              <svg
                className="mt-0.5 size-4 shrink-0 text-accentGreen"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div className="pr-2">
                    <p className="font-arimo text-sm font-medium text-gray-900">
                      {item.name}
                    </p>
                    {item.description && (
                      <p className="mt-1 font-arimo text-xs text-gray-600">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 font-arimo text-sm font-semibold text-gray-900">
                    {formatCents(item.price)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <label
              key={item.id}
              className={`flex cursor-pointer items-start space-x-3 rounded-lg p-3 transition-colors ${
                disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-white'
              }`}
            >
              <input
                id={`line-item-${item.id}`}
                name={`line-item-${item.id}`}
                type="checkbox"
                checked={selectedLineItems[item.id] || false}
                onChange={() => !disabled && onLineItemToggle(item.id)}
                disabled={disabled}
                className="mt-0.5 size-4 rounded border-gray-300 text-accentGreen focus:ring-accentGreen disabled:cursor-not-allowed"
                aria-label={item.name}
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div className="pr-2">
                    <p className="font-arimo text-sm font-medium text-gray-900">
                      {item.name}
                    </p>
                    {item.description && (
                      <p className="mt-1 font-arimo text-xs text-gray-600">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 font-arimo text-sm font-semibold text-gray-900">
                    {formatCents(item.price)}
                  </span>
                </div>
              </div>
            </label>
          ),
        )}
      </div>

      {/* Subtotal */}
      <div className="mt-4 flex items-center justify-between border-t border-gray-200/50 pt-4">
        <span className="font-arimo text-sm font-medium text-gray-700">
          Additional Items Subtotal:
        </span>
        <span className="font-oswald text-base font-bold text-gray-900 sm:text-lg">
          {formatCents(calculateSubtotal())}
        </span>
      </div>
    </div>
  );
};

export default ProposalLineItems;
